/** @typedef {import('@datahooks/types/organisationOverview.types.js').OrgOverviewMatrixRow} OrgOverviewMatrixRow */
import React from 'react';

import { Link } from 'react-router-dom';

import { TestableCard, TestableText } from '@components/general/TestableComponents';
import AvatarLogo from '@components/general/AvatarLogo';
import StyledTooltip from '@components/general/StyledTooltip';
import { featureDetails, toSentenceCase } from '@datahooks/metadata/DataUtils';

import LoadingIcon from '@components/navigation/LoadingIcon';

import PropTypes from 'prop-types';

/**
 * Colour classes by status for the health matrix cells
 */
const statusClasses = {
  good: 'bg-green-500',
  fair: 'bg-yellow-400',
  poor: 'bg-orange-400',
  fail: 'bg-red-400',
  na: 'bg-gray-200',
};

/**
 * Normalises the matrix value to one of the expected status values for consistent styling. 
 * If the value is not recognised, it defaults to 'na' (not available). 
 * This ensures that any unexpected or new status values do not break the UI and are displayed as 'N/A'. 
 * 
 * @param {string} value
 * 
 * @returns {string}
 */
function normaliseStatus(value) {
  if (['good', 'fair', 'poor', 'fail'].includes(value)) {
    return value;
  }

  return 'na';
}

/**
 * Displays a health matrix for projects in the organisation overview page. It receives a list of projects and their corresponding health matrix data as props, and renders a grid showing the health status of each project across different test types. The component also includes tooltips with additional information about each test type's status and thresholds. 
 * 
 * @param {{
 *  projects?: Array<{projectId: number, title: string, short_name?: string}>,
 *  matrix?: OrgOverviewMatrixRow[],
 *  isLoading?: boolean
 * }} props
 */
export default function OrganisationOverviewHealthMatrix({ projects = [], matrix = [], isLoading }) {
  
  // Enrich the matrix data with the logo_location from the projects data, matching on project_id.
  const enrichedMatrix = matrix.map((row) => {
    const project = projects.find((project) => project.projectId === row.project_id);
    
    return {
      ...row,
      logo_location: project?.logo_location,
    };
  });

  // Filter the matrix to only include rows for projects that are in the current filtered projects list
  const filteredMatrix = enrichedMatrix.filter((row) => projects.some((project) => project.projectId === row.project_id));
  const testTypeCount = Object.keys(filteredMatrix[0]?.test_types || {}).length;
  const matrixColumnsStyle = { gridTemplateColumns: `repeat(${testTypeCount || 1}, minmax(0, 1fr))` };

  if (isLoading) {
    return (
      <TestableCard dataTestId="org-overview-health-matrix-card" className="p-4 w-full">
        <div className="flex w-full justify-center">
          <LoadingIcon showText />
        </div>
      </TestableCard>
    );
  }

  return (
    <TestableCard dataTestId="org-overview-health-matrix-card" className="p-0 text-blue-500">
      <div className="p-4 md:p-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
        <div>
          <TestableText dataTestId="org-overview-health-matrix-title" variant="h3">
            Project Health Metric
          </TestableText>
          <TestableText dataTestId="org-overview-health-matrix-subtitle" variant="small" className="mt-1">
            Monitor the health status of every project across all test types.
          </TestableText>
        </div>
      </div>

      <div className="p-4 md:p-5 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,1fr)] gap-4 items-start">
          <div className="w-full space-y-2">
            <div className="space-y-2" data-testid="org-overview-health-matrix-grid">
              {filteredMatrix.map((project) => {

                return (
                  <div
                    data-testid={`org-overview-health-matrix-row-${project.project_name}`}
                    key={project.project_name}
                    className="grid grid-cols-[minmax(0,2fr)_minmax(0,7fr)] gap-3 items-stretch"
                  >
                    <div className="flex items-center justify-end gap-2 pr-2 min-w-0 py-2">
                      
                      <span className="text-sm whitespace-normal break-words leading-4 text-right min-w-0">
                        <StyledTooltip content="View project details" placement="top">
                          <Link
                            data-testid={`overview-project-matrix-${project.project_id}`}
                            key={project.project_name}
                            to={`/projects/${project.project_id}`}
                          >
                            {project.project_short_name}
                          </Link>
                        </StyledTooltip>
                      </span>
                      
                      <AvatarLogo alt={project.project_name} size="xs" displayType="avatar" src={project.logo_location} />

                    </div>

                    <div className="grid gap-1" style={matrixColumnsStyle}>
                      {Object.entries(project.test_types || {}).map(([testTypeId, testType]) => {

                        // Build the tooltip based on each cell's test type, value, and thresholds.
                        let tooltip = `${testType.testType}: ${testType.matrixValue ? toSentenceCase(testType.matrixValue) : 'N/A'}`;

                        if (testType.matrixValue !== 'fail') {  // If the test type failed, we just show the standard tooltip message without thresholds

                          // Add the unit to the tooltip value (except 'count' as that is implied)
                          let unit = ' ';

                          if (testType.unit !== 'count') {
                            unit = ` ${testType.unit}`;
                          }

                          if (testType.currentValue != null && testType.unit) {
                            tooltip += `\n\n${testType.label}:`;
                            tooltip += ` ${testType.currentValue}${unit}`;
                          }

                          // If thresholds are available for the test type, add them to the tooltip as well - each project/test type could have
                          // different threshold values so we cannot display a single key, we need to customise the tooltip for each cell
                          if (testType.thresholds) {
                            const { good, fair, poor } = testType.thresholds;
                            let displayUnit = '';

                            if (testType.unit !== 'count') {
                              displayUnit = ` ${testType.unit}`;
                            }

                            tooltip += ` \n\n - Good: ${good || 'N/A'}${displayUnit}\n - Fair: ${fair || 'N/A'}${displayUnit}\n - Poor: ${poor || 'N/A'}${displayUnit}`;
                          }
                        }

                        return (
                          <Link
                            key={`${project.project_id}-${testTypeId}`}
                            to={`/projects/${project.project_id}/releases/${project.current_release_id}/tabs/${testTypeId}`}
                            data-testid={`org-overview-health-matrix-status-link-${project.project_id}-${testTypeId}`}
                          >
                            <StyledTooltip content={tooltip} placement="top">
                              <div
                                data-testid={`org-overview-health-matrix-status-${project.project_id}-${testTypeId}`}
                                className={`h-10 rounded-sm ${statusClasses[normaliseStatus(testType?.matrixValue)]} w-full`}
                              />
                            </StyledTooltip>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,7fr)] gap-3 mt-2">
              <div className="text-center"></div>
              <div className="grid gap-1" style={matrixColumnsStyle}>
                {Object.entries(filteredMatrix[0]?.test_types || {}).map(([testTypeId, testType]) => {

                  // Create a column for each test type. With a popup info button
                  const featureDetail = featureDetails[testTypeId];
                  const PopoverComponent = featureDetail?.popover;

                  return (
                    <div key={testTypeId} className="min-w-0 text-sm">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
                        <span className="whitespace-normal leading-4 text-center">{testType?.testType || testTypeId}</span>
                        <span className="justify-self-end leading-none">
                          {PopoverComponent && <PopoverComponent page size={20} className="h20" />}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex lg:justify-start pl-5">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${statusClasses['good']}`} />
                <span className="font-normal">Excellent</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${statusClasses['fair']}`} />
                <span className="font-normal">Fair</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${statusClasses['poor']}`} />
                <span className="font-normal">Poor</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${statusClasses['fail']}`} />
                <span className="font-normal">Fail</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${statusClasses['na']}`} />
                <span className="font-normal">N/A</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TestableCard>
  );
}

OrganisationOverviewHealthMatrix.propTypes = {
  projects: PropTypes.array,
  matrix: PropTypes.array,
  isLoading: PropTypes.bool,
};
